import React, { useEffect, useState } from 'react';
import { orderService } from '../services/orderService';
import { X, ShieldCheck } from 'lucide-react';

const AVAILABLE_PINCODES = new Set([
  '201009', '201001', '201002', '201003', '201004', '201005', '201006', '201007', '201010', '201011',
  '201012', '201013', '201014', '201015', '201016', '201017', '201018', '201019', '201020', '201021',
  '110002', '110003', '110005', '110006', '110008', '110031', '110051', '110091', '110092', '110096',
  '110098', '110007', '110084', '110053', '110054', '110093', '110094', '110009', '110033', '110034',
  '110035', '110042', '110052', '110083', '110085', '110086', '110088', '110089', '110032', '110095',
  '110017', '110048', '110062', '110070', '110074', '110080', '110014', '110019', '110020', '110024',
  '110025', '110044', '110065', '110076', '110010', '110030', '110037', '110045', '110046', '110075',
  '110077', '110078', '110015', '110016', '110018', '110026', '110027', '110028', '110041', '110058',
  '110059', '110063', '110064', '110057', '110021', '201301', '201307', '201305', '201306', '201316',
  '201303', '201309', '201318'
]);

const DELIVERY_TIME_SLOTS = [
  '10:00 AM - 12:00 PM',
  '12:00 PM - 2:00 PM',
  '2:00 PM - 4:00 PM',
  '4:00 PM - 6:00 PM',
  '6:00 PM - 8:00 PM'
];

export default function OrderModal({ isOpen, onClose, selectedPlan, onSuccess, initialOrderType = 'New SIM' }) {
  const [formData, setFormData] = useState({
    customer_name: '',
    phone: '',
    email: '',
    address: '',
    city: 'Delhi',
    state: 'Delhi',
    pincode: '',
    order_type: 'New SIM',
    preferred_delivery_date: '',
    preferred_delivery_time: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) setFormData(current => ({ ...current, order_type: initialOrderType }));
  }, [isOpen, initialOrderType]);

  const normalizedPincode = formData.pincode.trim();
  const isPincodeAvailable = AVAILABLE_PINCODES.has(normalizedPincode);
  const isPincodeUnavailable = normalizedPincode.length === 6 && !AVAILABLE_PINCODES.has(normalizedPincode);
  const isBsnlPlan = String(selectedPlan?.operator || '').trim().toLowerCase() === 'bsnl';
  const deliveryCharge = formData.order_type === 'New SIM' || (formData.order_type === 'Port-in' && isBsnlPlan) ? 50 : 0;
  const today = new Date();
  const minimumDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  if (!isOpen || !selectedPlan) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPincodeAvailable) return;
    setLoading(true);
    try {
      const payload = {
        ...formData,
        operator: selectedPlan.operator,
        plan_id: selectedPlan.id,
        plan_name: selectedPlan.name,
        plan_price: selectedPlan.price,
        delivery_charge: deliveryCharge,
        total_amount: Number(selectedPlan.price || 0) + deliveryCharge
      };
      const created = await orderService.createOrder(payload);
      onClose();
      if (onSuccess) onSuccess(created);
    } catch (err) {
      alert('Order failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-[9990] p-4 animate-in fade-in duration-200">
      <div className="glass-modal max-w-lg w-full rounded-3xl max-h-[90vh] overflow-y-auto p-6 sm:p-8 shadow-2xl border border-white/80">
        
        {/* Header */}
        <div className="flex justify-between items-center border-b border-slate-200/60 pb-3 mb-5">
          <div>
            <h2 className="text-xl font-black text-slate-900">Doorstep Delivery Details</h2>
            <p className="text-xs text-orange-600 font-bold mt-0.5">
              {selectedPlan.operator} - {selectedPlan.name} (₹{selectedPlan.price})
            </p>
            <div className="mt-2 text-xs font-semibold text-slate-600">
              Plan amount: ₹{selectedPlan.price}
              <span className="mx-1.5 text-slate-300">|</span>
              Delivery charge: {deliveryCharge ? '₹50' : 'Free'}
              <span className="mx-1.5 text-slate-300">|</span>
              <span className="font-black text-slate-900">Total: ₹{Number(selectedPlan.price || 0) + deliveryCharge}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3.5 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block font-bold text-slate-700 uppercase mb-1 text-[11px]">Full Name *</label>
              <input 
                type="text" 
                required 
                placeholder="Aman Sharma" 
                value={formData.customer_name} 
                onChange={e => setFormData({...formData, customer_name: e.target.value})} 
                className="w-full px-3.5 py-2.5 rounded-xl bg-white/70 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500 font-medium" 
              />
            </div>
            <div>
              <label className="block font-bold text-slate-700 uppercase mb-1 text-[11px]">Mobile Number *</label>
              <input 
                type="tel" 
                required 
                pattern="[0-9]{10}" 
                placeholder="10-digit mobile number" 
                value={formData.phone} 
                onChange={e => setFormData({...formData, phone: e.target.value})} 
                className="w-full px-3.5 py-2.5 rounded-xl bg-white/70 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500 font-medium" 
              />
            </div>
          </div>

          <div>
            <label className="block font-bold text-slate-700 uppercase mb-1 text-[11px]">Email Address</label>
            <input 
              type="email" 
              placeholder="you@example.com" 
              value={formData.email} 
              onChange={e => setFormData({...formData, email: e.target.value})} 
              className="w-full px-3.5 py-2.5 rounded-xl bg-white/70 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500 font-medium" 
            />
          </div>

          <div>
            <label className="block font-bold text-slate-700 uppercase mb-1 text-[11px]">Order Type</label>
            <select 
              value={formData.order_type} 
              onChange={e => setFormData({...formData, order_type: e.target.value})} 
              className="w-full px-3.5 py-2.5 rounded-xl bg-white/70 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500 font-medium"
            >
              <option value="New SIM">New SIM Connection</option>
              <option value="Port-in">Port SIM (Keep existing number MNP)</option>
              <option value="SIM Replacement">SIM Replacement / Upgrade</option>
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-700 uppercase mb-1 text-[11px]">Delivery Address *</label>
            <input 
              type="text" 
              required 
              placeholder="House/Flat No, Street, Area" 
              value={formData.address} 
              onChange={e => setFormData({...formData, address: e.target.value})} 
              className="w-full px-3.5 py-2.5 rounded-xl bg-white/70 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500 font-medium" 
            />
          </div>

          <div className="grid grid-cols-3 gap-2.5">
            <div>
              <label className="block font-bold text-slate-700 uppercase mb-1 text-[11px]">City</label>
              <input 
                type="text" 
                required 
                value={formData.city} 
                onChange={e => setFormData({...formData, city: e.target.value})} 
                className="w-full px-3 py-2.5 rounded-xl bg-white/70 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500 font-medium" 
              />
            </div>
            <div>
              <label className="block font-bold text-slate-700 uppercase mb-1 text-[11px]">State</label>
              <input 
                type="text" 
                required 
                value={formData.state} 
                onChange={e => setFormData({...formData, state: e.target.value})} 
                className="w-full px-3 py-2.5 rounded-xl bg-white/70 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500 font-medium" 
              />
            </div>
            <div>
              <label className="block font-bold text-slate-700 uppercase mb-1 text-[11px]">Pincode *</label>
              <input 
                type="text" 
                required 
                pattern="[0-9]{6}" 
                placeholder="110001" 
                value={formData.pincode} 
                onChange={e => setFormData({...formData, pincode: e.target.value})} 
                className="w-full px-3 py-2.5 rounded-xl bg-white/70 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500 font-medium" 
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block font-bold text-slate-700 uppercase mb-1 text-[11px]">SIM Requirement Date *</label>
              <input
                type="date"
                required
                min={minimumDate}
                value={formData.preferred_delivery_date}
                onChange={e => setFormData({ ...formData, preferred_delivery_date: e.target.value })}
                className="w-full px-3.5 py-2.5 rounded-xl bg-white/70 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500 font-medium"
              />
            </div>
            <div>
              <label className="block font-bold text-slate-700 uppercase mb-1 text-[11px]">SIM Requirement Time *</label>
              <select
                required
                value={formData.preferred_delivery_time}
                onChange={e => setFormData({ ...formData, preferred_delivery_time: e.target.value })}
                className="w-full px-3.5 py-2.5 rounded-xl bg-white/70 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500 font-medium"
              >
                <option value="">Select a time slot</option>
                {DELIVERY_TIME_SLOTS.map(slot => <option key={slot} value={slot}>{slot}</option>)}
              </select>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !isPincodeAvailable}
            className="w-full mt-4 py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-sm shadow-md shadow-orange-500/25 transition-all flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:from-orange-500 disabled:hover:to-orange-600"
          >
            <ShieldCheck className="w-5 h-5" />
            {loading ? 'Submitting Order...' : 'Confirm Doorstep Delivery'}
          </button>
          {isPincodeUnavailable && (
            <p className="-mt-2 text-center text-[11px] font-bold text-red-600" role="alert">
              Delivery not available for this location
            </p>
          )}
        </form>
      </div>
    </div>
  );
}